import { useCallback, useEffect, useState } from "react";

export type AuthRole = "admin" | "customer";

export type AuthUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: AuthRole;
  createdAt: string;
};

const STORAGE_KEY = "purenest_auth_user";
const USERS_DB_KEY = "purenest_users_db";
const SEEDED_KEY = "purenest_seeded_admin";

type StoredUser = AuthUser & { password: string };

const readUsers = (): StoredUser[] => {
  try {
    const raw = localStorage.getItem(USERS_DB_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredUser[];
    // Backfill role for users created before role was introduced
    return parsed.map((u) => ({ ...u, role: u.role ?? "customer" }));
  } catch {
    return [];
  }
};

const writeUsers = (list: StoredUser[]) => {
  localStorage.setItem(USERS_DB_KEY, JSON.stringify(list));
};

const sanitize = (u: StoredUser): AuthUser => {
  const { password: _password, ...rest } = u;
  return rest;
};

const seedAdminIfNeeded = () => {
  if (localStorage.getItem(SEEDED_KEY) === "1") return;
  const users = readUsers();
  const hasAdmin = users.some((u) => u.role === "admin");
  if (!hasAdmin) {
    const adminUser: StoredUser = {
      id: "u_admin_demo",
      firstName: "Avery",
      lastName: "Holloway",
      email: "admin@purenest.com",
      password: "admin123",
      role: "admin",
      createdAt: new Date().toISOString(),
    };
    writeUsers([...users, adminUser]);
  }
  localStorage.setItem(SEEDED_KEY, "1");
};

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    seedAdminIfNeeded();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AuthUser;
        // Backfill role for sessions from before role existed
        if (!parsed.role) parsed.role = "customer";
        setUser(parsed);
      }
    } catch {
      // ignore
    }
    setReady(true);
  }, []);

  const persist = (next: AuthUser | null) => {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
    setUser(next);
  };

  const signUp = useCallback(
    async (payload: { firstName: string; lastName: string; email: string; password: string }) => {
      await new Promise((r) => setTimeout(r, 600));
      const users = readUsers();
      const exists = users.find((u) => u.email.toLowerCase() === payload.email.toLowerCase());
      if (exists) {
        throw new Error("An account with that email already exists.");
      }
      const newUser: StoredUser = {
        id: `u_${Date.now()}`,
        firstName: payload.firstName.trim(),
        lastName: payload.lastName.trim(),
        email: payload.email.trim().toLowerCase(),
        password: payload.password,
        role: "customer",
        createdAt: new Date().toISOString(),
      };
      writeUsers([...users, newUser]);
      const sanitized = sanitize(newUser);
      persist(sanitized);
      return sanitized;
    },
    [],
  );

  const signIn = useCallback(async (email: string, password: string) => {
    await new Promise((r) => setTimeout(r, 600));
    const users = readUsers();
    const found = users.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
    if (!found || found.password !== password) {
      throw new Error("Email or password is incorrect.");
    }
    const sanitized = sanitize(found);
    persist(sanitized);
    return sanitized;
  }, []);

  const signInAsAdmin = useCallback(async (email: string, password: string) => {
    await new Promise((r) => setTimeout(r, 600));
    const users = readUsers();
    const found = users.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
    if (!found || found.password !== password) {
      throw new Error("Email or password is incorrect.");
    }
    if (found.role !== "admin") {
      throw new Error("This account doesn't have admin access.");
    }
    const sanitized = sanitize(found);
    persist(sanitized);
    return sanitized;
  }, []);

  const signOut = useCallback(() => {
    persist(null);
  }, []);

  return { user, ready, signUp, signIn, signInAsAdmin, signOut };
}